import {
  Component,
  JSXElement,
  For,
  Show,
  createSignal,
  createEffect,
  onCleanup,
} from 'solid-js';
import DropdownMenu, { DropdownMenuOption } from '../DropDownMenu/DropDownMenu';
import { DropDownOption } from '../DropdownOptions/DropdownOptions';
import Icon from '../Icon/Icon';
import { PROJECT_PLACEHOLDER_THUMBNAIL } from '../constants';
import { MODUS_CARD_CHROME_RESET_STYLESHEET } from '../../mc-custom-css/mc-card-chrome-reset.css';
import { injectInRenderContext } from '../util';

export interface CardItem {
  id: string;
  title: string;
  imageUrl?: string;
  isActive?: boolean;
  dropdownOptions?: DropDownOption[];
}

export type CardsLayout = 'vertical' | 'horizontal';
export type CardsPadding = 'compact' | 'comfortable';

export interface CardsProps {
  cards?: CardItem[];
  children?: JSXElement;
  containerRef?(el: HTMLDivElement): void;
  defaultImageUrl?: string;
  class?: string;
  clickable?: boolean;
  columnClasses?: string;
  onCardClick?(card: CardItem): void;
  onDropdownOptionSelect?(cardId: string, option: string, index: number): void;
  bordered?: boolean;
  layout?: CardsLayout;
  padding?: CardsPadding;
  customClass?: string;
  /** When true, children render without modus-wc-card chrome (style-host only). */
  bare?: boolean;
  customStylesheet?: string;
  /** When false (default), the modus-wc-card chrome is hidden via injected styles. */
  visibleChrome?: boolean;
}

const toModusOptions = (
  dropdownOptions: DropDownOption[]
): DropdownMenuOption[] =>
  dropdownOptions
    .filter((opt) => opt.text)
    .map((opt) => ({
      label: opt.text as string,
      value: opt.text as string,
      startIcon: opt.icon,
      hasSubmenu: opt.hasSubMenu || undefined,
    }));

const Cards: Component<CardsProps> = (props): JSXElement => {
  let cardsRef: HTMLElement | undefined;
  const [activeDropdownCard, setActiveDropdownCard] = createSignal<
    string | null
  >(null);

  createEffect(() => {
    const css = props.customStylesheet;
    if (!css) {
      return;
    }
    let cancelled = false;
    const inject = (): void => {
      if (cancelled) {
        return;
      }
      if (!cardsRef) {
        requestAnimationFrame(inject);
        return;
      }
      injectInRenderContext(cardsRef, 'cards-custom-style', css);
    };
    inject();
    onCleanup(() => {
      cancelled = true;
    });
  });

  createEffect(() => {
    if (props.visibleChrome) return;
    let cancelled = false;
    const inject = (): void => {
      if (cancelled) return;
      if (!cardsRef) {
        requestAnimationFrame(inject);
        return;
      }
      injectInRenderContext(
        cardsRef,
        'cards-chrome-reset-style',
        MODUS_CARD_CHROME_RESET_STYLESHEET
      );
    };
    inject();
    onCleanup(() => {
      cancelled = true;
    });
  });

  const getCards = (): CardItem[] => props.cards ?? [];
  const getDefaultImageUrl = (): string =>
    props.defaultImageUrl || PROJECT_PLACEHOLDER_THUMBNAIL;
  const getClassName = (): string => props.class || '';
  const getClickable = (): boolean => props.clickable ?? false;
  const getColumnClasses = (): string =>
    props.columnClasses || 'col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3';

  const handleCardClick = (card: CardItem): void => {
    if (getClickable() && props.onCardClick) {
      props.onCardClick(card);
    }
  };

  const handleDropdownMenuOpen = (cardId: string, isVisible: boolean): void => {
    setActiveDropdownCard(isVisible ? cardId : null);
  };

  const handleDropdownOptionSelect = (
    cardId: string,
    selected: string,
    index: number
  ): void => {
    setActiveDropdownCard(null);
    if (props.onDropdownOptionSelect) {
      props.onDropdownOptionSelect(cardId, selected, index);
    }
  };

  const renderGridCard = (card: CardItem): JSXElement => {
    const getCustomClass = (): string => {
      const classes: string[] = [];
      if (card.isActive === true) {
        classes.push('active');
      }
      if (activeDropdownCard() === card.id) {
        classes.push('dropdown-open');
      }
      if (getClickable()) {
        classes.push('cursor-pointer');
      }
      return classes.join(' ');
    };

    return (
      <div class={getColumnClasses()}>
        <modus-wc-card
          layout={props.layout ?? 'vertical'}
          bordered={props.bordered ?? true}
          padding={props.padding ?? 'compact'}
          custom-class={getCustomClass()}
          onClick={() => {
            handleCardClick(card);
          }}
        >
          <div slot="header">
            <img
              src={card.imageUrl || getDefaultImageUrl()}
              alt={card.title}
              style="width: 100%; height: auto; object-fit: cover;"
            />
          </div>
          <h4 slot="title" class="text-semibold">
            {card.title}
          </h4>
          <Show when={card.dropdownOptions && card.dropdownOptions.length > 0}>
            <div
              slot="actions"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <DropdownMenu
                buttonVariant="borderless"
                buttonColor="tertiary"
                buttonShape="square"
                buttonSize="md"
                buttonAriaLabel="Card actions"
                menuPlacement="bottom-end"
                options={toModusOptions(card.dropdownOptions!)}
                onVisibilityChanged={(isVisible) => {
                  handleDropdownMenuOpen(card.id, isVisible);
                }}
                onSelectionChanged={(selected, index) => {
                  if (index === undefined) return;
                  handleDropdownOptionSelect(card.id, selected, index);
                }}
              >
                <span slot="trigger">
                  <Icon icon="tc-icon-more-vert" size="icon-medium" />
                </span>
              </DropdownMenu>
            </div>
          </Show>
        </modus-wc-card>
      </div>
    );
  };

  const renderChildrenBare = (): JSXElement => (
    <div ref={props.containerRef} style={{ display: 'contents' }}>
      {props.children}
    </div>
  );

  const renderChildrenCard = (): JSXElement => (
    <modus-wc-card
      bordered={props.bordered ?? false}
      layout={props.layout ?? 'vertical'}
      padding={props.padding ?? 'compact'}
      custom-class={props.customClass ?? ''}
    >
      <div ref={props.containerRef} style={{ display: 'contents' }}>
        {props.children}
      </div>
    </modus-wc-card>
  );

  return (
    <div
      ref={(el) => {
        cardsRef = el;
      }}
      style={{ display: 'contents' }}
    >
      <Show
        when={props.cards !== undefined}
        fallback={props.bare ? renderChildrenBare() : renderChildrenCard()}
      >
        <div class={`tile ${getClassName()}`.trim()}>
          <div class="row">
            <For each={getCards()}>{(card) => renderGridCard(card)}</For>
          </div>
        </div>
      </Show>
    </div>
  );
};

export default Cards;
